'use client';

import { useEffect, useMemo, useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  UTM_PARAM_KEYS,
  TRACKING_PARAM_KEYS,
  buildAnchorLink,
  cleanupUtmParams,
  compareUrlAnalyses,
  decodeTextByMode,
  encodeTextByMode,
  inspectCampaignUrl,
  parseUrlAnalysis,
  removeParamsFromAnalysis,
  type CampaignUrlInspection,
  type ParsedUrlAnalysis,
  type UrlComparisonResult,
  type UrlQueryParam,
  type UrlTransformMode,
} from '@/lib/browser-link-tools';

type Variant =
  | 'utm-extractor'
  | 'remove-url-parameters'
  | 'url-decoder'
  | 'url-encoder'
  | 'query-parameter-parser'
  | 'compare-two-urls'
  | 'utm-cleaner'
  | 'campaign-url-inspector'
  | 'anchor-link-builder';

type ToolResult =
  | {
      kind: 'utm-extractor';
      analysis: ParsedUrlAnalysis;
      plainText: string;
      jsonText: string;
    }
  | {
      kind: 'remove-url-parameters';
      analysis: ParsedUrlAnalysis;
      cleanedUrl: string;
      removedKeys: string[];
      plainText: string;
    }
  | {
      kind: 'url-decoder' | 'url-encoder';
      output: string;
      mode: UrlTransformMode;
      warning: string | null;
    }
  | {
      kind: 'query-parameter-parser';
      analysis: ParsedUrlAnalysis;
      plainText: string;
      jsonText: string;
    }
  | {
      kind: 'compare-two-urls';
      left: ParsedUrlAnalysis;
      right: ParsedUrlAnalysis;
      comparison: UrlComparisonResult;
      plainText: string;
      jsonText: string;
    }
  | {
      kind: 'utm-cleaner';
      analysis: ParsedUrlAnalysis;
      cleanedUrl: string;
      plainText: string;
    }
  | {
      kind: 'campaign-url-inspector';
      analysis: ParsedUrlAnalysis;
      inspection: CampaignUrlInspection;
      plainText: string;
      jsonText: string;
    }
  | {
      kind: 'anchor-link-builder';
      baseUrl: string;
      rawFragment: string;
      normalizedFragment: string;
      finalUrl: string;
      warning: string | null;
      plainText: string;
    };

const transformModes: Array<{ value: UrlTransformMode; label: string }> = [
  { value: 'full-url', label: 'Full URL' },
  { value: 'query-string', label: 'Query string' },
  { value: 'query-value', label: 'Query value' },
  { value: 'path-segment', label: 'Path segment' },
];

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
    primaryPlaceholder: string;
    secondaryPlaceholder?: string;
    rawFragmentPlaceholder?: string;
  }
> = {
  'utm-extractor': {
    title: 'Extract UTM parameters from one campaign URL',
    description:
      'Paste one full URL, inspect the UTM fields, and review the extra non-UTM query parameters before copying the parsed result.',
    badge: 'UTM',
    actionLabel: 'Extract UTMs',
    successLabel: 'UTM extraction',
    primaryPlaceholder:
      'https://example.com/landing?utm_source=google&utm_medium=cpc&utm_campaign=spring-sale',
  },
  'remove-url-parameters': {
    title: 'Remove selected URL parameters without breaking the destination',
    description:
      'Paste one full URL, choose the query parameters to remove, and export a cleaner final URL while keeping the fragment in place.',
    badge: 'Clean',
    actionLabel: 'Clean URL',
    successLabel: 'URL cleanup',
    primaryPlaceholder: 'https://example.com/page?utm_source=google&fbclid=123&ref=twitter#pricing',
  },
  'url-decoder': {
    title: 'Decode URL-encoded text into a readable value',
    description:
      'Paste encoded text, choose the decoding mode, and turn the URL, query string, query value, or path segment back into readable text.',
    badge: 'Decode',
    actionLabel: 'Decode text',
    successLabel: 'URL decode',
    primaryPlaceholder: 'utm_campaign=spring%20launch&ref=partner%2Falpha',
  },
  'url-encoder': {
    title: 'Encode plain text for URL-safe use',
    description:
      'Paste a URL part or plain text, choose the encoding mode, and get one copy-ready encoded result for safer links and query strings.',
    badge: 'Encode',
    actionLabel: 'Encode text',
    successLabel: 'URL encode',
    primaryPlaceholder: 'spring launch / partner alpha',
  },
  'query-parameter-parser': {
    title: 'Parse one URL into path, fragment, and ordered query params',
    description:
      'Inspect the origin, path, fragment, and query parameter structure of one URL, including duplicate keys and empty values.',
    badge: 'Query',
    actionLabel: 'Parse URL',
    successLabel: 'URL parsing',
    primaryPlaceholder: 'https://example.com/search?q=cleanor&q=browser&empty=&flag#features',
  },
  'compare-two-urls': {
    title: 'Compare two URLs and highlight the real differences',
    description:
      'Paste two full URLs to inspect changes in origin, path, fragment, and query parameters without losing track of added or removed values.',
    badge: 'Compare',
    actionLabel: 'Compare URLs',
    successLabel: 'URL comparison',
    primaryPlaceholder: 'https://example.com/page?utm_source=google&utm_campaign=spring',
    secondaryPlaceholder:
      'https://example.com/page?utm_source=meta&utm_campaign=spring-sale&ref=partner',
  },
  'utm-cleaner': {
    title: 'Remove only UTM tags and keep the rest of the query string',
    description:
      'Paste one full URL, strip only the UTM parameters, and keep all non-UTM query values and the fragment intact.',
    badge: 'UTM clean',
    actionLabel: 'Remove UTMs',
    successLabel: 'UTM cleanup',
    primaryPlaceholder: 'https://example.com/page?utm_source=google&utm_medium=cpc&sku=123#details',
  },
  'campaign-url-inspector': {
    title: 'Inspect one campaign URL for missing UTMs and tracking clutter',
    description:
      'Audit one marketing URL for missing required UTM values, duplicate UTMs, and extra tracking parameters before launch or QA.',
    badge: 'Inspect',
    actionLabel: 'Inspect URL',
    successLabel: 'Campaign inspection',
    primaryPlaceholder: 'https://example.com/offer?utm_source=google&utm_medium=cpc&fbclid=123',
  },
  'anchor-link-builder': {
    title: 'Build a clean URL with a slugified #anchor fragment',
    description:
      'Enter the base URL and the heading or anchor text, then generate a normalized fragment and one copy-ready anchored URL.',
    badge: 'Anchor',
    actionLabel: 'Generate anchor link',
    successLabel: 'Anchor link generation',
    primaryPlaceholder: 'https://example.com/docs/getting-started',
    rawFragmentPlaceholder: 'Pricing & plans',
  },
};

function tryParseUrl(value: string) {
  if (!value.trim()) {
    return { analysis: null, error: null as string | null };
  }

  try {
    return { analysis: parseUrlAnalysis(value), error: null as string | null };
  } catch (error) {
    return {
      analysis: null,
      error: error instanceof Error ? error.message : 'The URL could not be parsed.',
    };
  }
}

function createTextDownload(text: string, fileName: string, mimeType = 'text/plain;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

function buildAnalysisJson(analysis: ParsedUrlAnalysis) {
  return JSON.stringify(
    {
      baseUrl: analysis.baseUrl,
      origin: analysis.origin,
      path: analysis.path,
      fragment: analysis.fragment,
      duplicateKeys: analysis.duplicateKeys,
      emptyValueKeys: analysis.emptyValueKeys,
      bareKeys: analysis.bareKeys,
      orderedParams: analysis.orderedParams.map((entry) => ({
        key: entry.key,
        value: entry.value,
        hasValue: entry.hasValue,
        repeated: entry.isRepeated,
        utm: entry.isUtm,
        tracking: entry.isTracking,
      })),
      utmParams: analysis.utmParams,
    },
    null,
    2,
  );
}

function formatParamList(entries: UrlQueryParam[]) {
  if (!entries.length) {
    return 'None';
  }

  return entries
    .map((entry) => `${entry.key}${entry.hasValue ? `=${entry.value}` : ''}`)
    .join('\n');
}

function buildUtmExtractorText(analysis: ParsedUrlAnalysis) {
  return [
    `Base URL: ${analysis.baseUrl}`,
    `Fragment: ${analysis.fragment || '(none)'}`,
    ...UTM_PARAM_KEYS.map(
      (key) => `${key}: ${(analysis.utmParams[key] ?? []).join(', ') || '(missing)'}`,
    ),
    '',
    'Extra parameters:',
    formatParamList(analysis.extraParams),
  ].join('\n');
}

function buildParserText(analysis: ParsedUrlAnalysis) {
  return [
    `Origin: ${analysis.origin}`,
    `Path: ${analysis.path}`,
    `Base URL: ${analysis.baseUrl}`,
    `Fragment: ${analysis.fragment || '(none)'}`,
    `Duplicate keys: ${analysis.duplicateKeys.join(', ') || 'None'}`,
    `Empty values: ${analysis.emptyValueKeys.join(', ') || 'None'}`,
    `Bare keys: ${analysis.bareKeys.join(', ') || 'None'}`,
    '',
    'Ordered parameters:',
    formatParamList(analysis.orderedParams),
  ].join('\n');
}

function buildComparisonText(
  left: ParsedUrlAnalysis,
  right: ParsedUrlAnalysis,
  comparison: UrlComparisonResult,
) {
  return [
    `Origin changed: ${comparison.originChanged ? 'Yes' : 'No'}`,
    `Path changed: ${comparison.pathChanged ? 'Yes' : 'No'}`,
    `Fragment changed: ${comparison.fragmentChanged ? 'Yes' : 'No'}`,
    '',
    `Left URL: ${left.normalizedUrl}`,
    `Right URL: ${right.normalizedUrl}`,
    '',
    `Added params: ${comparison.addedParams.map((item) => item.key).join(', ') || 'None'}`,
    `Removed params: ${comparison.removedParams.map((item) => item.key).join(', ') || 'None'}`,
    `Changed params: ${comparison.changedParams.map((item) => item.key).join(', ') || 'None'}`,
    `Repeated-key differences: ${comparison.repeatedKeyDifferences.join(', ') || 'None'}`,
  ].join('\n');
}

function buildCampaignInspectionText(
  analysis: ParsedUrlAnalysis,
  inspection: CampaignUrlInspection,
) {
  return [
    `Destination URL: ${inspection.destinationUrl}`,
    `Status: ${inspection.statusLabels.join(', ')}`,
    `Missing required UTMs: ${inspection.missingRequiredUtms.join(', ') || 'None'}`,
    `Duplicate UTM keys: ${inspection.duplicateUtmKeys.join(', ') || 'None'}`,
    `Extra tracking params: ${inspection.extraTrackingParams.map((entry) => entry.key).join(', ') || 'None'}`,
    '',
    ...UTM_PARAM_KEYS.map(
      (key) => `${key}: ${(analysis.utmParams[key] ?? []).join(', ') || '(missing)'}`,
    ),
  ].join('\n');
}

export default function BrowserLinkTextTool({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [primaryInput, setPrimaryInput] = useState('');
  const [secondaryInput, setSecondaryInput] = useState('');
  const [fragmentInput, setFragmentInput] = useState('');
  const [transformMode, setTransformMode] = useState<UrlTransformMode>('full-url');
  const [removalPreset, setRemovalPreset] = useState<'tracking' | 'utm' | 'all' | 'custom'>(
    'tracking',
  );
  const [selectedParamKeys, setSelectedParamKeys] = useState<string[]>([]);
  const [result, setResult] = useState<ToolResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  const primaryPreview = useMemo(() => {
    if (
      variant === 'url-decoder' ||
      variant === 'url-encoder' ||
      !primaryInput.trim() ||
      variant === 'anchor-link-builder'
    ) {
      return { analysis: null, error: null as string | null };
    }

    return tryParseUrl(primaryInput);
  }, [primaryInput, variant]);

  const secondaryPreview = useMemo(() => {
    if (variant !== 'compare-two-urls' || !secondaryInput.trim()) {
      return { analysis: null, error: null as string | null };
    }

    return tryParseUrl(secondaryInput);
  }, [secondaryInput, variant]);

  useEffect(() => {
    if (variant !== 'remove-url-parameters') {
      return;
    }

    const analysis = primaryPreview.analysis;
    if (!analysis) {
      setSelectedParamKeys([]);
      return;
    }

    if (removalPreset === 'all') {
      setSelectedParamKeys(analysis.orderedParams.map((entry) => entry.key));
      return;
    }

    if (removalPreset === 'utm') {
      setSelectedParamKeys([
        ...new Set(analysis.orderedParams.filter((entry) => entry.isUtm).map((entry) => entry.key)),
      ]);
      return;
    }

    if (removalPreset === 'tracking') {
      setSelectedParamKeys([
        ...new Set(
          analysis.orderedParams
            .filter((entry) => TRACKING_PARAM_KEYS.includes(entry.key as never))
            .map((entry) => entry.key),
        ),
      ]);
    }
  }, [primaryPreview.analysis, removalPreset, variant]);

  function resetFeedback() {
    setErrorMessage(null);
    setCreditMessage(null);
  }

  async function runTool() {
    resetFeedback();
    setIsProcessing(true);

    try {
      let nextResult: ToolResult;

      if (variant === 'utm-extractor') {
        const analysis = parseUrlAnalysis(primaryInput);
        nextResult = {
          kind: variant,
          analysis,
          plainText: buildUtmExtractorText(analysis),
          jsonText: buildAnalysisJson(analysis),
        };
      } else if (variant === 'remove-url-parameters') {
        const analysis = parseUrlAnalysis(primaryInput);
        const removedKeys = [...new Set(selectedParamKeys)];
        const cleanedUrl = removeParamsFromAnalysis(analysis, removedKeys);
        nextResult = {
          kind: variant,
          analysis,
          cleanedUrl,
          removedKeys,
          plainText: [
            `Cleaned URL: ${cleanedUrl}`,
            `Removed keys: ${removedKeys.join(', ') || 'None'}`,
          ].join('\n'),
        };
      } else if (variant === 'url-decoder') {
        const output = decodeTextByMode(primaryInput, transformMode);
        nextResult = {
          kind: variant,
          output: output.value,
          mode: transformMode,
          warning: output.warning,
        };
      } else if (variant === 'url-encoder') {
        nextResult = {
          kind: variant,
          output: encodeTextByMode(primaryInput, transformMode),
          mode: transformMode,
          warning: null,
        };
      } else if (variant === 'query-parameter-parser') {
        const analysis = parseUrlAnalysis(primaryInput);
        nextResult = {
          kind: variant,
          analysis,
          plainText: buildParserText(analysis),
          jsonText: buildAnalysisJson(analysis),
        };
      } else if (variant === 'compare-two-urls') {
        const left = parseUrlAnalysis(primaryInput);
        const right = parseUrlAnalysis(secondaryInput);
        const comparison = compareUrlAnalyses(left, right);
        nextResult = {
          kind: variant,
          left,
          right,
          comparison,
          plainText: buildComparisonText(left, right, comparison),
          jsonText: JSON.stringify(
            { left: buildAnalysisJson(left), right: buildAnalysisJson(right), comparison },
            null,
            2,
          ),
        };
      } else if (variant === 'utm-cleaner') {
        const analysis = parseUrlAnalysis(primaryInput);
        const cleanedUrl = cleanupUtmParams(analysis);
        nextResult = {
          kind: variant,
          analysis,
          cleanedUrl,
          plainText: [`Cleaned URL: ${cleanedUrl}`, 'Removed: UTM parameters only'].join('\n'),
        };
      } else if (variant === 'campaign-url-inspector') {
        const analysis = parseUrlAnalysis(primaryInput);
        const inspection = inspectCampaignUrl(analysis);
        nextResult = {
          kind: variant,
          analysis,
          inspection,
          plainText: buildCampaignInspectionText(analysis, inspection),
          jsonText: JSON.stringify(
            { analysis: JSON.parse(buildAnalysisJson(analysis)), inspection },
            null,
            2,
          ),
        };
      } else if (variant === 'anchor-link-builder') {
        const anchor = buildAnchorLink(primaryInput, fragmentInput);
        nextResult = {
          kind: variant,
          baseUrl: anchor.baseUrl,
          rawFragment: fragmentInput.trim(),
          normalizedFragment: anchor.normalizedFragment,
          finalUrl: anchor.finalUrl,
          warning: anchor.warning,
          plainText: [
            `Base URL: ${anchor.baseUrl}`,
            `Raw fragment: ${fragmentInput.trim()}`,
            `Normalized fragment: ${anchor.normalizedFragment}`,
            `Final URL: ${anchor.finalUrl}`,
          ].join('\n'),
        };
      } else {
        throw new Error('This link tool variant is not configured.');
      }

      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another link tool run right now.',
      );
      setResult(nextResult);

      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setResult(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'The link tool could not finish this request.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleCopy(text: string, successMessage: string) {
    try {
      await copyText(text);
      setCreditMessage(successMessage);
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The browser could not copy the current result.',
      );
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.description}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>
              {variant === 'anchor-link-builder'
                ? 'Base URL'
                : variant === 'compare-two-urls'
                  ? 'First URL'
                  : 'Input'}
            </span>
            <textarea
              onChange={(event) => {
                setPrimaryInput(event.target.value);
                setResult(null);
                resetFeedback();
              }}
              placeholder={copy.primaryPlaceholder}
              rows={variant === 'url-decoder' || variant === 'url-encoder' ? 5 : 4}
              value={primaryInput}
            />
          </label>

          {variant === 'compare-two-urls' ? (
            <label className="tool-live-field tool-live-field-full">
              <span>Second URL</span>
              <textarea
                onChange={(event) => {
                  setSecondaryInput(event.target.value);
                  setResult(null);
                  resetFeedback();
                }}
                placeholder={copy.secondaryPlaceholder}
                rows={4}
                value={secondaryInput}
              />
            </label>
          ) : null}

          {variant === 'anchor-link-builder' ? (
            <label className="tool-live-field tool-live-field-full">
              <span>Anchor text or fragment</span>
              <input
                onChange={(event) => {
                  setFragmentInput(event.target.value);
                  setResult(null);
                  resetFeedback();
                }}
                placeholder={copy.rawFragmentPlaceholder}
                type="text"
                value={fragmentInput}
              />
            </label>
          ) : null}

          {variant === 'url-decoder' || variant === 'url-encoder' ? (
            <label className="tool-live-field">
              <span>Mode</span>
              <select
                onChange={(event) => {
                  setTransformMode(event.target.value as UrlTransformMode);
                  setResult(null);
                  resetFeedback();
                }}
                value={transformMode}
              >
                {transformModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {variant === 'remove-url-parameters' && primaryPreview.analysis ? (
            <>
              <label className="tool-live-field">
                <span>Preset</span>
                <select
                  onChange={(event) => {
                    setRemovalPreset(event.target.value as 'tracking' | 'utm' | 'all' | 'custom');
                    setResult(null);
                    resetFeedback();
                  }}
                  value={removalPreset}
                >
                  <option value="tracking">Tracking params</option>
                  <option value="utm">UTM params only</option>
                  <option value="all">Remove all query params</option>
                  <option value="custom">Custom selection</option>
                </select>
              </label>

              <div className="tool-live-preview">
                <h3>Parameters to review</h3>
                <div className="tool-live-stats">
                  {primaryPreview.analysis.orderedParams.map((entry, index) => {
                    const id = `${entry.key}-${entry.index}-${index}`;
                    const isChecked = selectedParamKeys.includes(entry.key);
                    return (
                      <label className="tool-live-field" htmlFor={id} key={id}>
                        <span>
                          <input
                            checked={isChecked}
                            id={id}
                            onChange={(event) => {
                              setRemovalPreset('custom');
                              setSelectedParamKeys((current) =>
                                event.target.checked
                                  ? [...new Set([...current, entry.key])]
                                  : current.filter((key) => key !== entry.key),
                              );
                              setResult(null);
                              resetFeedback();
                            }}
                            type="checkbox"
                          />{' '}
                          {entry.key}
                        </span>
                        <small>{entry.hasValue ? entry.value || '(empty)' : '(bare key)'}</small>
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={isProcessing}
              onClick={() => void runTool()}
              type="button"
            >
              {isProcessing ? 'Processing…' : copy.actionLabel}
            </button>

            {result?.kind === 'utm-extractor' ? (
              <>
                <button
                  className="button button-ghost"
                  onClick={() => void handleCopy(result.plainText, 'UTM summary copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-ghost"
                  onClick={() => void handleCopy(result.jsonText, 'UTM JSON copied.')}
                  type="button"
                >
                  Copy JSON
                </button>
              </>
            ) : null}

            {result?.kind === 'remove-url-parameters' ? (
              <button
                className="button button-ghost"
                onClick={() => void handleCopy(result.cleanedUrl, 'Cleaned URL copied.')}
                type="button"
              >
                Copy cleaned URL
              </button>
            ) : null}

            {result?.kind === 'url-decoder' || result?.kind === 'url-encoder' ? (
              <button
                className="button button-ghost"
                onClick={() => void handleCopy(result.output, 'URL text copied.')}
                type="button"
              >
                Copy output
              </button>
            ) : null}

            {result?.kind === 'query-parameter-parser' ? (
              <>
                <button
                  className="button button-ghost"
                  onClick={() => void handleCopy(result.plainText, 'Parsed URL summary copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-ghost"
                  onClick={() => void handleCopy(result.jsonText, 'Parsed URL JSON copied.')}
                  type="button"
                >
                  Copy JSON
                </button>
              </>
            ) : null}

            {result?.kind === 'compare-two-urls' ? (
              <>
                <button
                  className="button button-ghost"
                  onClick={() => void handleCopy(result.plainText, 'URL comparison copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-ghost"
                  onClick={() => void handleCopy(result.jsonText, 'URL comparison JSON copied.')}
                  type="button"
                >
                  Copy JSON
                </button>
              </>
            ) : null}

            {result?.kind === 'utm-cleaner' ? (
              <button
                className="button button-ghost"
                onClick={() => void handleCopy(result.cleanedUrl, 'UTM-cleaned URL copied.')}
                type="button"
              >
                Copy cleaned URL
              </button>
            ) : null}

            {result?.kind === 'campaign-url-inspector' ? (
              <>
                <button
                  className="button button-ghost"
                  onClick={() => void handleCopy(result.plainText, 'Campaign inspection copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-ghost"
                  onClick={() =>
                    void handleCopy(result.jsonText, 'Campaign inspection JSON copied.')
                  }
                  type="button"
                >
                  Copy JSON
                </button>
              </>
            ) : null}

            {result?.kind === 'anchor-link-builder' ? (
              <button
                className="button button-ghost"
                onClick={() => void handleCopy(result.finalUrl, 'Anchor link copied.')}
                type="button"
              >
                Copy anchor link
              </button>
            ) : null}
          </div>

          {result ? (
            <div className="hero-actions tool-live-actions">
              {'plainText' in result ? (
                <button
                  className="button button-ghost"
                  onClick={() => createTextDownload(result.plainText, `${variant}.txt`)}
                  type="button"
                >
                  Download text
                </button>
              ) : null}
              {'jsonText' in result ? (
                <button
                  className="button button-ghost"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      `${variant}.json`,
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              ) : null}
            </div>
          ) : null}

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Input summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Primary input</strong>
              <span>{primaryInput.trim() ? 'Ready' : 'Not provided yet'}</span>
            </div>
            <div>
              <strong>Mode</strong>
              <span>
                {variant === 'url-decoder' || variant === 'url-encoder'
                  ? transformModes.find((mode) => mode.value === transformMode)?.label
                  : variant === 'compare-two-urls'
                    ? 'Two URL compare'
                    : variant === 'anchor-link-builder'
                      ? 'Base URL + fragment'
                      : 'Single URL'}
              </span>
            </div>
            <div>
              <strong>Params</strong>
              <span>
                {primaryPreview.analysis
                  ? `${primaryPreview.analysis.orderedParams.length} detected`
                  : variant === 'url-decoder' || variant === 'url-encoder'
                    ? 'Text transform'
                    : 'Analyze to inspect'}
              </span>
            </div>
            <div>
              <strong>Privacy</strong>
              <span>Processed in your browser</span>
            </div>
          </div>

          {primaryPreview.error ? (
            <div className="tool-live-panel-note">
              <strong>Primary URL</strong>
              <span>{primaryPreview.error}</span>
            </div>
          ) : null}
          {secondaryPreview.error ? (
            <div className="tool-live-panel-note">
              <strong>Second URL</strong>
              <span>{secondaryPreview.error}</span>
            </div>
          ) : null}
          {primaryPreview.analysis?.warning ? (
            <div className="tool-live-panel-note">
              <strong>Normalization note</strong>
              <span>{primaryPreview.analysis.warning}</span>
            </div>
          ) : null}
        </aside>
      </div>

      {result?.kind === 'utm-extractor' ? (
        <div className="tool-live-preview">
          <h3>UTM details</h3>
          <div className="tool-live-stats">
            {UTM_PARAM_KEYS.map((key) => (
              <div key={key}>
                <strong>{key}</strong>
                <span>{(result.analysis.utmParams[key] ?? []).join(', ') || 'Missing'}</span>
              </div>
            ))}
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>Extra non-UTM params</span>
            <textarea readOnly rows={5} value={formatParamList(result.analysis.extraParams)} />
          </label>
        </div>
      ) : null}

      {result?.kind === 'remove-url-parameters' ? (
        <div className="tool-live-preview">
          <h3>Cleaned URL</h3>
          <label className="tool-live-field tool-live-field-full">
            <span>Result</span>
            <textarea readOnly rows={4} value={result.cleanedUrl} />
          </label>
        </div>
      ) : null}

      {result?.kind === 'url-decoder' || result?.kind === 'url-encoder' ? (
        <div className="tool-live-preview">
          <h3>Output</h3>
          <label className="tool-live-field tool-live-field-full">
            <span>Transformed text</span>
            <textarea readOnly rows={6} value={result.output} />
          </label>
          {result.warning ? (
            <p className="support-feedback support-feedback-error">{result.warning}</p>
          ) : null}
        </div>
      ) : null}

      {result?.kind === 'query-parameter-parser' ? (
        <div className="tool-live-preview">
          <h3>Parsed structure</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Origin</strong>
              <span>{result.analysis.origin}</span>
            </div>
            <div>
              <strong>Path</strong>
              <span>{result.analysis.path}</span>
            </div>
            <div>
              <strong>Fragment</strong>
              <span>{result.analysis.fragment || 'None'}</span>
            </div>
            <div>
              <strong>Duplicate keys</strong>
              <span>{result.analysis.duplicateKeys.join(', ') || 'None'}</span>
            </div>
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>Ordered query params</span>
            <textarea readOnly rows={8} value={formatParamList(result.analysis.orderedParams)} />
          </label>
        </div>
      ) : null}

      {result?.kind === 'compare-two-urls' ? (
        <div className="tool-live-preview">
          <h3>Comparison result</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Origin changed</strong>
              <span>{result.comparison.originChanged ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <strong>Path changed</strong>
              <span>{result.comparison.pathChanged ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <strong>Fragment changed</strong>
              <span>{result.comparison.fragmentChanged ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <strong>Changed params</strong>
              <span>
                {result.comparison.changedParams.map((item) => item.key).join(', ') || 'None'}
              </span>
            </div>
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>Comparison summary</span>
            <textarea readOnly rows={9} value={result.plainText} />
          </label>
        </div>
      ) : null}

      {result?.kind === 'utm-cleaner' ? (
        <div className="tool-live-preview">
          <h3>UTM-cleaned URL</h3>
          <label className="tool-live-field tool-live-field-full">
            <span>Result</span>
            <textarea readOnly rows={4} value={result.cleanedUrl} />
          </label>
        </div>
      ) : null}

      {result?.kind === 'campaign-url-inspector' ? (
        <div className="tool-live-preview">
          <h3>Campaign inspection</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Status</strong>
              <span>{result.inspection.statusLabels.join(', ')}</span>
            </div>
            <div>
              <strong>Missing UTMs</strong>
              <span>{result.inspection.missingRequiredUtms.join(', ') || 'None'}</span>
            </div>
            <div>
              <strong>Duplicate UTMs</strong>
              <span>{result.inspection.duplicateUtmKeys.join(', ') || 'None'}</span>
            </div>
            <div>
              <strong>Extra tracking</strong>
              <span>
                {result.inspection.extraTrackingParams.map((entry) => entry.key).join(', ') ||
                  'None'}
              </span>
            </div>
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>Inspection summary</span>
            <textarea readOnly rows={8} value={result.plainText} />
          </label>
        </div>
      ) : null}

      {result?.kind === 'anchor-link-builder' ? (
        <div className="tool-live-preview">
          <h3>Anchor link</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Raw fragment</strong>
              <span>{result.rawFragment || 'None'}</span>
            </div>
            <div>
              <strong>Normalized fragment</strong>
              <span>{result.normalizedFragment}</span>
            </div>
            <div>
              <strong>Base URL</strong>
              <span>{result.baseUrl}</span>
            </div>
            <div>
              <strong>Final URL</strong>
              <span>{result.finalUrl}</span>
            </div>
          </div>
          {result.warning ? (
            <div className="tool-live-panel-note">
              <strong>Normalization note</strong>
              <span>{result.warning}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
